import { ArrowRight } from "lucide-react";
import { Reveal, SectionHead, GoldBtn, PageHero, Particles, Marquee } from "@/components/Shared";
import Dome from "@/components/Dome";
import PhoneMockup from "@/components/PhoneMockup";
import { PAVILIONS, POD_CAPABILITIES, MAHATALKS_TOPICS, IMPACT_FLOW, IMPACT_FOCUS, EVENINGS, DIGITAL_FEATURES } from "@/data/content";

export default function Experience() {
    return (
        <div data-testid="experience-page">
            <PageHero
                testid="experience-hero"
                eyebrow="The Experience"
                title={<>Enter the <span className="gold-text">Digital MahaBazaar</span></>}
                sub="Pavilions, pods, domes, addas and arenas — a festival architecture built for discovery. Wander through the worlds inside the bazaar."
            >
                <GoldBtn to="/register" testid="exp-register-btn">Register Now</GoldBtn>
                <GoldBtn to="/venue#map" variant="outline" testid="exp-map-btn">Open Event Map</GoldBtn>
            </PageHero>

            <Marquee items={["10+ Thematic Pavilions", "120+ Startups", "Startup Pods", "MahaTalks", "Investor Adda", "Impact Kshetra", "Founder's Journey Dome"]} />

            {/* PAVILIONS */}
            <section id="pavilions" className="py-24 md:py-32 scroll-mt-28">
                <div className="container-x">
                    <SectionHead eyebrow="The Innovation Bazaar" title={<>Eleven Pavilions. <span className="gold-text">One Marketplace of the Future.</span></>}
                        sub="Interactive experiences — AR demos, digital displays and live storytelling corners — across the full geography of Indian innovation." />
                    <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {PAVILIONS.map((p, i) => (
                            <Reveal key={p.n} delay={(i % 3) * 80}>
                                <div className="card-luxe group relative p-8 h-full overflow-hidden" data-testid={`exp-pavilion-${p.n}`}>
                                    <div className="absolute inset-x-0 top-0 h-[3px]" style={{ background: `linear-gradient(90deg, transparent, ${p.tint}, transparent)` }} />
                                    <div className="card-glow" />
                                    <div className="flex items-center justify-between">
                                        <div className="w-12 h-14 arch-top border flex items-center justify-center" style={{ borderColor: `${p.tint}66` }}>
                                            <p.icon size={20} strokeWidth={1.2} style={{ color: p.tint }} />
                                        </div>
                                        <span className="font-display text-3xl text-[var(--ivory)]/20 group-hover:text-[var(--gold)]/70 transition-colors">{p.n}</span>
                                    </div>
                                    <h3 className="font-display text-2xl mt-7 text-[var(--ivory)]">{p.name}</h3>
                                    <p className="mt-3 text-sm text-muted-luxe leading-relaxed">{p.desc}</p>
                                    <a href="/register?type=startup" className="mt-6 inline-flex items-center gap-2 text-[0.6rem] tracking-[0.3em] uppercase text-[var(--gold)] group-hover:gap-3.5 transition-all">
                                        Explore Pavilion <ArrowRight size={11} />
                                    </a>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* STARTUP PODS */}
            <section id="pods" className="py-24 md:py-32 bg-[#1C060C]/60 scroll-mt-28 noise relative overflow-hidden">
                <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
                    <Reveal>
                        <span className="eyebrow no-lines">Startup Pods</span>
                        <h2 className="font-display font-medium text-4xl sm:text-5xl leading-[1.06] mt-6">
                            Ideas Have <span className="gold-text">a Home.</span>
                        </h2>
                        <p className="mt-6 text-muted-luxe text-base md:text-lg leading-relaxed">
                            Compact plug-and-play pods — every startup arrives to a ready stage. Tell your story,
                            run live demos, and let investors find you with a scan.
                        </p>
                        <div className="mt-10 space-y-6">
                            {POD_CAPABILITIES.map((c) => (
                                <div key={c.n} className="flex gap-5 items-start" data-testid={`exp-pod-${c.n}`}>
                                    <div className="w-11 h-11 shrink-0 border border-[var(--line)] flex items-center justify-center">
                                        <c.icon size={17} strokeWidth={1.3} className="text-[var(--gold)]" />
                                    </div>
                                    <div>
                                        <h4 className="text-sm tracking-[0.14em] uppercase text-[var(--ivory)]">{c.n} · {c.title}</h4>
                                        <p className="text-sm text-muted-luxe mt-1.5 leading-relaxed">{c.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-10">
                            <GoldBtn to="/register?type=startup" testid="exp-pod-cta">Showcase Your Startup</GoldBtn>
                        </div>
                    </Reveal>
                    <Reveal delay={160} className="relative">
                        <div className="card-luxe p-10">
                            <div className="grid grid-cols-3 gap-4">
                                {Array.from({ length: 9 }).map((_, i) => (
                                    <div key={i} className={`aspect-square border flex items-center justify-center ${i === 4 ? "border-[var(--gold)] bg-[rgba(74,14,24,0.4)]" : "hairline bg-[rgba(38,9,16,0.6)]"}`}>
                                        <span className={`font-display ${i === 4 ? "gold-text text-xl" : "text-[var(--ivory)]/25"}`}>
                                            {i === 4 ? "YOU" : `P${i + 1}`}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <p className="mt-6 text-center text-[0.58rem] tracking-[0.34em] uppercase text-muted-luxe">The Pod District · Innovation Bazaar</p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* MAHATALKS */}
            <section id="mahatalks" className="py-24 md:py-32 scroll-mt-28">
                <div className="container-x">
                    <SectionHead eyebrow="MahaTalks" title={<>The Voices <span className="gold-text">of Bharat</span></>}
                        sub="Dialogues, debates, deep dives and conversations across three days — exploring the future of entrepreneurship and innovation." />
                    <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {MAHATALKS_TOPICS.map((t, i) => (
                            <Reveal key={t} delay={(i % 3) * 70}>
                                <div className="border hairline p-6 h-full hover:border-[var(--line)] hover:bg-[rgba(201,162,75,0.04)] transition-all duration-500">
                                    <span className="font-display text-sm text-[var(--gold)]/70">Session 0{i + 1}</span>
                                    <h3 className="font-display text-xl mt-3 text-[var(--ivory)]">{t}</h3>
                                    <p className="mt-2 text-[0.58rem] tracking-[0.3em] uppercase text-muted-luxe">MahaTalks Stage · Speakers to be announced</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal className="mt-12 text-center">
                        <GoldBtn to="/programme" variant="outline" testid="exp-programme-btn">View Full Programme</GoldBtn>
                    </Reveal>
                </div>
            </section>

            {/* DOME */}
            <section id="dome" className="py-24 md:py-32 bg-[#1C060C]/60 scroll-mt-28 noise relative overflow-hidden">
                <Particles density={25} />
                <div className="container-x text-center">
                    <SectionHead eyebrow="The Founder's Journey Dome" title={<>From First Idea <span className="gold-text">to Impact</span></>}
                        sub="A 360° projection theatre telling startup journeys — step inside the dome and walk the path every founder knows." />
                    <Reveal delay={150} className="mt-14 max-w-4xl mx-auto"><Dome /></Reveal>
                </div>
            </section>

            {/* IMPACT KSHETRA */}
            <section id="impact" className="py-24 md:py-32 scroll-mt-28">
                <div className="container-x text-center">
                    <SectionHead eyebrow="Impact Kshetra" title={<>The <span className="gold-text">Future</span> Pavilion</>}
                        sub="A hub for social and environmental innovation — changemakers solving the toughest problems for Bharat and the world." />
                    <div className="mt-16 flex flex-wrap items-center justify-center gap-y-6">
                        {IMPACT_FLOW.map((s, i) => (
                            <div key={s} className="flex items-center">
                                <Reveal delay={i * 110}>
                                    <div className="px-6 py-4 border border-[var(--line)] bg-[rgba(38,9,16,0.7)] min-w-[8.5rem]">
                                        <span className="block text-[0.55rem] tracking-[0.34em] text-[var(--gold)] uppercase">0{i + 1}</span>
                                        <span className="block font-display text-lg text-[var(--ivory)] mt-1">{s}</span>
                                    </div>
                                </Reveal>
                                {i < IMPACT_FLOW.length - 1 && <span className="hidden md:block w-10 h-px mx-1 shimmer-line" />}
                            </div>
                        ))}
                    </div>
                    <Reveal delay={140}>
                        <div className="mt-12 flex flex-wrap justify-center gap-3">
                            {IMPACT_FOCUS.map((f) => (
                                <span key={f} className="px-4 py-2 border hairline text-[0.6rem] tracking-[0.28em] uppercase text-muted-luxe">{f}</span>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* DIGITAL EXPERIENCE */}
            <section className="py-24 md:py-32 bg-[#1C060C]/60">
                <div className="container-x grid lg:grid-cols-2 gap-16 items-center">
                    <Reveal className="order-2 lg:order-1"><PhoneMockup /></Reveal>
                    <Reveal delay={140} className="order-1 lg:order-2">
                        <span className="eyebrow no-lines">Digital Experience</span>
                        <h2 className="font-display font-medium text-4xl sm:text-5xl leading-[1.06] mt-6">
                            The MahaBazaar <span className="gold-text">in Your Pocket</span>
                        </h2>
                        <p className="mt-6 text-muted-luxe text-base md:text-lg leading-relaxed">
                            Navigate the bazaar with a conceptual event companion — from live agenda to PitchYatra
                            voting and your digital badge.
                        </p>
                        <div className="mt-9 grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {DIGITAL_FEATURES.map((f) => (
                                <div key={f.title} className="border hairline px-4 py-3.5">
                                    <f.icon size={16} strokeWidth={1.3} className="text-[var(--gold)]" />
                                    <p className="mt-2.5 text-[0.62rem] tracking-[0.18em] uppercase text-[var(--ivory)]/80">{f.title}</p>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* EVENINGS */}
            <section id="evenings" className="py-24 md:py-32 scroll-mt-28 noise relative overflow-hidden"
                style={{ background: "linear-gradient(180deg, #140409, #1B0710 55%, #140409)" }}>
                <Particles density={35} />
                <div className="container-x">
                    <SectionHead eyebrow="Evening Experiences" title={<>When Business Meets <span className="gold-text">Celebration</span></>} />
                    <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {EVENINGS.map((e, i) => (
                            <Reveal key={e.name} delay={i * 80}>
                                <div className="card-luxe tilt p-7 h-full">
                                    <div className="card-glow" />
                                    <div className="w-11 h-11 arch-top border border-[var(--line)] flex items-center justify-center">
                                        <e.icon size={18} strokeWidth={1.3} className="text-[var(--champagne)]" />
                                    </div>
                                    <h3 className="font-display text-xl mt-6 text-[var(--ivory)]">{e.name}</h3>
                                    <p className="mt-3 text-xs text-muted-luxe leading-relaxed">{e.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
